import { computed, effect, inject, Injectable, linkedSignal, signal } from '@angular/core';

import { AlertsStore } from '../../alerts/alerts.store';
import { ToolboxStore } from '../../toolbox/toolbox.store';
import { SiteListRow, SITES_START_OFFSET } from '../models/site-list';
import { SITE_LIST_VIEW_MODES, SiteListViewMode } from '../models/SiteListViewMode';
import { SiteListResource } from '../resource/site-list-resource';
import { LoadedSitesPage } from '../resource/site-list-resource.types';
import { HttpStatsService } from '../services/http-stats.service';
import { buildSiteListRows } from '../utils/site-list-row.utils';

@Injectable({ providedIn: 'root' })
export class SiteListFacade {
    private readonly siteList = inject(SiteListResource);
    private readonly toolboxes = inject(ToolboxStore);
    private readonly alertsStore = inject(AlertsStore);
    private readonly statsService = inject(HttpStatsService);

    readonly items = this.siteList.items;
    readonly rows = computed<SiteListRow[]>(() =>
        buildSiteListRows(
            this.siteList.items(),
            (gatewayIds) => this.toolboxes.isToolbox(gatewayIds),
            this.alertsStore.alertsSeverityBySites(),
        ),
    );
    readonly totalCount = this.siteList.totalCount;
    readonly keyword = this.siteList.keyword;
    readonly loading = this.siteList.loading;
    readonly refreshing = this.siteList.refreshing;
    readonly error = this.siteList.error;
    readonly viewMode = signal<SiteListViewMode>(SITE_LIST_VIEW_MODES.list);
    /** All the installer's installations, for the dashboard tab: a search or a load in flight keeps the last total. */
    readonly siteCount = linkedSignal<LoadedSitesPage | undefined, number>({
        source: this.siteList.lastLoadedPage,
        computation: (page, previous) =>
            page?.request.keyword === '' ? page.response.totalCount : (previous?.value ?? 0),
    }).asReadonly();

    constructor() {
        effect(() => {
            const loadedPage = this.siteList.lastLoadedPage();
            if (!loadedPage) return;

            const { request, response } = loadedPage;
            if (request.offset === SITES_START_OFFSET && request.keyword.length === 0) {
                void this.updateSitesStats(response.totalCount);
            }
        });
    }

    pageOpened(): void {
        void this.toolboxes.load();
        this.alertsStore.reload();
        if (this.siteList.lastLoadedPage()) {
            this.siteList.reload();
        }
    }

    loadMore(): void {
        this.siteList.loadMore();
    }

    reload(): void {
        this.siteList.reload();
        void this.toolboxes.reload();
        this.alertsStore.reload();
    }

    search(keyword: string): void {
        this.siteList.search(keyword);
    }

    private async updateSitesStats(count: number): Promise<void> {
        try {
            await this.statsService.updateSitesStats(count);
        } catch {
            // Statistics are best-effort and must not replace a successful site response with an error.
        }
    }
}
